// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';

// Local imports
import readableId from './ReadableId.js';
import { getProductShortDescription } from './dashboard/utils/product.js';

// The follow-up email for a sent quote, shared by the homepage follow-up queue and the quote's
// own status line so both send the same email.

export function followUpRoute(o) {
    const r = (o.routes || [])[0];
    const src = r?.source?.text || r?.source?.id || '';
    const dst = r?.destination?.text || r?.destination?.id || '';
    if (src && dst) return `${src} → ${dst}`;
    return dst || src || 'your shipment';
}

// Follow-up timing is measured from when the quote was actually sent to the customer: the
// latest 'sent-to-endcustomer' log entry (each send/resend writes one). Fall back to the
// opportunity's creation time for legacy quotes recorded before that log entry existed.
export function sentTimestamp(o) {
    const sent = (o.log || []).filter(l => l.action === 'sent-to-endcustomer' && l.timestamp).map(l => l.timestamp);
    return sent.length ? _.max(sent) : o.created;
}

export function buildFollowUpEmail({ opportunity: o, accounts }) {
    const account = accounts?.find(a => a.id === o.account?.id);
    // Send the follow-up to whoever the quote was last sent to: each send (including resends
    // after a reopen) writes a 'sent-to-endcustomer' log entry with the recipients in `reason`.
    // Use the latest one's TO recipients; fall back to the account contacts if it was never sent.
    let toRecipients = [];
    const lastSentLog = _.findLast(o.log, l => l.action === 'sent-to-endcustomer');
    if (lastSentLog && lastSentLog.reason) {
        try {
            toRecipients = (JSON.parse(lastSentLog.reason).toRecipients || []).filter(r => r.address);
        } catch (e) { toRecipients = []; }
    }
    if (!toRecipients.length) {
        toRecipients = (account?.contacts || [])
            .map(c => ({ address: c.email, name: _.trim(`${c.firstName || ''} ${c.lastName || ''}`) }))
            .filter(r => r.address);
    }
    const route = followUpRoute(o);
    const ts = sentTimestamp(o);
    const date = ts ? dayjs(ts).format('MMM D, YYYY') : '';
    const method = o.transportationMethod ? _.startCase(o.transportationMethod) : '';
    // Cargo, per product: a short product summary (e.g. "2 x 40' HC", "lcl packages"), followed by
    // its commodities when it has any ("1 x 40' Dry: battery"). Deciding once for the whole quote
    // dropped every product without a commodity as soon as one product had one.
    // Containers and trucks keep the commodity on the product; LCL and air freight keep it on each
    // package (calculations).
    const cargoOf = p => {
        const commodities = _.uniq([p.commodity, ...(p.calculations || []).map(c => c.commodity)]
            .map(c => String(c || '').trim()).filter(c => c && !/^general$/i.test(c)));
        const summary = getProductShortDescription({ product: p });
        return commodities.length ? `${summary}: ${commodities.join(', ')}` : summary;
    };
    const cargo = _.uniq((o.products || []).map(cargoOf).filter(Boolean)).join(', ');
    const hasRoute = route !== 'your shipment';
    const subject = `Hey, following up on your ${method ? `${method} ` : ''}quote${cargo ? ` for ${cargo}` : ''}${hasRoute ? ` (${route})` : ''}`;
    const body = `Hi,<br/><br/>`
        + `I wanted to quickly follow up on our quote <b>${readableId(o)}</b>${date ? ` sent on <b>${date}</b>` : ''}`
        + ` for <b>${cargo || 'your shipment'}</b>${method ? ` shipped by <b>${method}</b>` : ''}${hasRoute ? ` on <b>${route}</b>` : ''}.<br/><br/>`
        + `The quote is attached, and you can <a href="https://app.monada.ai/offers?temporaryAccesstoken=PLACEHOLDER">view it here</a>.<br/><br/>`
        + `Do you want to move forward with it? Happy to hear your thoughts.`;
    return { toRecipients, subject, body };
}
